import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToaster } from '../../context/ToasterContext';
import { getHeaders, getNextSentences } from '../../service/ApiService';
import { ACCESS_TOKEN } from '../../service/AuthService';

// Type declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeakTabProps {
  preFetchResponse: {selectedWordsString: string, message: string} | undefined;
  setPreFetchResponse: (preFetchResponse: {selectedWordsString: string, message: string} | undefined) => void;
  contents: string[];
  setContents: (contents: string[] | ((prev: string[]) => string[])) => void;
  isActive?: boolean;
}

const SpeakTab = (props: SpeakTabProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  let currentAudio: HTMLAudioElement | null = null;
  const [audioReady, setAudioReady] = useState(false);
  const [pendingAudio, setPendingAudio] = useState<HTMLAudioElement | null>(null);
  const { contents, setContents, isActive = false } = props;
  const { showError, showSuccess } = useToaster();
  const navigate = useNavigate();

  //TODO: play multiple audio one by one
  const playMultipleAudioOneByOne = async () => {
    const audioUrls = [
      '/app/1.mp3', 
      '/app/2.mp3',
      '/app/3.mp3',
      '/app/4.mp3'
    ];
    
    for (const audioUrl of audioUrls) {
      try {
        const audio = new Audio(audioUrl);
        
        // Wait for the audio to finish playing before moving to the next
        await new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });
        
        // Small delay between audio files
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to play ${audioUrl}:`, error);
        showError(`Failed to play audio: ${audioUrl}`);
      }
    }
  }

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  };

  const preFetchNextSentences = async () => {
    const response = await getNextSentences({"amount": "5"});
    props.setPreFetchResponse(response);
  }

  // Scroll to bottom when tab becomes active or contents change
  useEffect(() => {
    if (isActive) {
      setTimeout(scrollToBottom, 100);
    }
  }, [isActive, contents]);

  const handleNext5Sentences = async () => {
    setIsLoading(true);

    try {
      let response = props.preFetchResponse;
      if (!response) {
        response = await getNextSentences({"amount": "5"});
      }
      const message = response.message;
      // Split response into array using '.' as separator
      const sentences = message.split('.').filter(sentence => sentence.trim().length > 0);
      setContents([...contents, '= = = = >', ...sentences]);

      // Call TTS API
      const ttsResponse = await axios.post('/api/get-sentence-speech',  response, { headers: getHeaders(), responseType: 'blob' });

      const blob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);

      // Create audio and attach to DOM or play directly
      const audio = new Audio(url);
      
      // Set volume to maximum (1.0) to increase the audio level
      audio.volume = 1.0;
      
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        URL.revokeObjectURL(currentAudio.src);
      }

      // Store the current audio instance
      currentAudio = audio;
      
      // Clean up when audio finishes
      audio.onended = () => {
        URL.revokeObjectURL(audio.src);
        if (currentAudio === audio) {
          currentAudio = null;
        }
      };
      
      // Play audio with proper error handling for autoplay restrictions
      try {
        await audio.play();
        setAudioReady(false);
        setPendingAudio(null);
      } catch (playError: any) {
        console.warn('Autoplay blocked:', playError.message);
        // Store the audio for manual play
        setPendingAudio(audio);
        setAudioReady(true);
        showError('Audio playback was blocked. Please tap the play button to hear the audio.');
      }

      setIsLoading(false);
      // Scroll to bottom after content is added
      setTimeout(scrollToBottom, 100);

      // pre-fetch next 5 sentences and set to preFetchResponse
      preFetchNextSentences();
    } catch (err: any) {
      //check status 401 and redirect to login page
      if (err.response?.status === 401) {
        localStorage.removeItem(ACCESS_TOKEN);
        navigate('/login');
      }
      if (err.response?.status === 429) {
        showError('You have reached the maximum number of requests for today. Please try again tomorrow.');
        setIsLoading(false);
        return;
      }
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get OpenAI response';
      console.error(errorMessage);
      showError(errorMessage);
      setIsLoading(false);
    } 
  };

  const playPendingAudio = async () => {
    if (pendingAudio) {
      try {
        // Ensure volume is set to maximum
        pendingAudio.volume = 1.0;
        await pendingAudio.play();
        setAudioReady(false);
        setPendingAudio(null);
      } catch (error) {
        console.error('Failed to play audio:', error);
        showError('Failed to play audio');
      }
    }
  };

  return (
    <div className="content speak">
      <div className="scroll-area" ref={scrollAreaRef}>
        {contents.map((c, i) => (
          <div key={i} className="speak-item">{c}</div>
        ))}
      </div>
      
      <button className="bottom-button" onClick={handleNext5Sentences} disabled={isLoading}>
        {isLoading ? (
          <>
            <svg className="loading-spinner" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C13.1 2 14 2.9 14 4V6.24C15.22 6.72 16 7.88 16 9.22V14C16 15.66 14.66 17 13 17H11C9.34 17 8 15.66 8 14V9.22C8 7.88 8.78 6.72 10 6.24V4C10 2.9 10.9 2 12 2Z"/>
            </svg>
            Loading...
          </>
        ) : (
          'Next 5 Sentences'
        )}
      </button>
      {audioReady && pendingAudio && (
        <button
          className="bottom-button"
          onClick={playPendingAudio}
          style={{ marginTop: '10px', backgroundColor: '#28a745' }}
        >
          ▶ Play Audio
        </button>
      )}
    </div>
  );
};

SpeakTab.displayName = 'SpeakTab';

export default SpeakTab;
